import { recordUnreadThreadOpen } from '#pplx/frontend/apps/ask/all-results/hooks/allResultsQuery.js';
import { optimisticallyMarkSidebarItemRead, optimisticallyMarkSidebarItemUnread, } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/sidebar-recent-items-state/sidebarRecentItemsState.js';
import { setThreadsUnreadInCache } from '#pplx/frontend/apps/ask/hooks/threads-hooks/threadCacheHelpers.js';
import { updateThreadMetadata } from '#pplx/frontend/apps/ask/hooks/threads-hooks/useRecentThreadUpdates.js';
import { invalidateNotificationLists } from '#pplx/frontend/apps/ask/layout/sidebar/core/shared/useAppNotificationMutations.js';
import { markThreadsAsUnreadMutation, markThreadsAsViewedMutation, } from '#pplx/frontend/apps/ask/queries/mutations/threadMutations.js';
import { getAutomationHasUnreadQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/automationKeys.js';
import { getUnreadNotificationCountQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/notificationKeys.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
// row navigation and the visible thread can save the same read through different hooks.
const pendingReadsByClient = new WeakMap();
function getPendingReads(queryClient) {
    let pendingReads = pendingReadsByClient.get(queryClient);
    if (!pendingReads) {
        pendingReads = new Map();
        pendingReadsByClient.set(queryClient, pendingReads);
    }
    return pendingReads;
}
export function beginPendingThreadRead(queryClient, contextUUID, optimisticUpdate, restoreUnread) {
    const pendingReads = getPendingReads(queryClient);
    const read = pendingReads.get(contextUUID) ?? {
        requests: 0,
        succeeded: false,
        listUpdates: [],
    };
    read.requests++;
    if (optimisticUpdate)
        read.listUpdates.push(optimisticUpdate);
    read.restoreUnread ??= restoreUnread;
    pendingReads.set(contextUUID, read);
    return (succeeded) => {
        if (pendingReads.get(contextUUID) !== read)
            return;
        read.succeeded ||= succeeded;
        if (--read.requests > 0)
            return;
        pendingReads.delete(contextUUID);
        for (const update of read.listUpdates.toReversed()) {
            if (read.succeeded)
                update.commit();
            else
                update.rollback();
        }
        if (!read.succeeded)
            read.restoreUnread?.();
    };
}
export function cancelPendingThreadReads(queryClient, contextUUIDs) {
    const pendingReads = getPendingReads(queryClient);
    for (const contextUUID of contextUUIDs) {
        const pendingRead = pendingReads.get(contextUUID);
        pendingReads.delete(contextUUID);
        for (const update of pendingRead?.listUpdates.toReversed() ?? [])
            update.rollback();
    }
}
export function useMarkThreadRead({ reason }) {
    const queryClient = useQueryClient();
    const markThreadsRead = useCallback((contextUUIDs, optimisticUpdate, restoreUnread) => {
        if (contextUUIDs.length === 0)
            return;
        const reads = contextUUIDs.map((contextUUID) => beginPendingThreadRead(queryClient, contextUUID, optimisticUpdate, restoreUnread));
        for (const contextUUID of contextUUIDs) {
            updateThreadMetadata(contextUUID, { unread: false });
            optimisticallyMarkSidebarItemRead(contextUUID);
        }
        setThreadsUnreadInCache(queryClient, contextUUIDs, false);
        markThreadsAsViewedMutation({ contextUUIDs, reason })
            .then(() => {
            for (const settle of reads)
                settle(true);
            void invalidateNotificationLists(queryClient);
            void queryClient.invalidateQueries({
                exact: true,
                queryKey: getUnreadNotificationCountQueryKey(),
            });
            // Automation results also open on ordinary task URLs, so the route cannot identify them.
            return queryClient.invalidateQueries({ queryKey: getAutomationHasUnreadQueryKey() });
        }, () => {
            for (const settle of reads)
                settle(false);
        })
            .catch(() => { });
    }, [queryClient, reason]);
    const markThreadsUnread = useCallback((contextUUIDs) => {
        if (contextUUIDs.length === 0)
            return;
        cancelPendingThreadReads(queryClient, contextUUIDs);
        for (const contextUUID of contextUUIDs) {
            updateThreadMetadata(contextUUID, { unread: true });
            optimisticallyMarkSidebarItemUnread(contextUUID);
        }
        setThreadsUnreadInCache(queryClient, contextUUIDs, true);
        markThreadsAsUnreadMutation({ contextUUIDs, reason }).catch(() => { });
    }, [queryClient, reason]);
    const markThreadOpenedFromList = useCallback(({ contextUUID, unread, onOptimisticRead }) => {
        if (!contextUUID || !unread)
            return;
        const listUpdate = onOptimisticRead?.();
        markThreadsRead([contextUUID], listUpdate, () => {
            updateThreadMetadata(contextUUID, { unread: true });
            optimisticallyMarkSidebarItemUnread(contextUUID);
            setThreadsUnreadInCache(queryClient, [contextUUID], true);
        });
        recordUnreadThreadOpen(contextUUID);
    }, [markThreadsRead, queryClient]);
    const markThreadUnread = useCallback(({ contextUUID, unread }) => {
        if (!contextUUID || unread)
            return;
        markThreadsUnread([contextUUID]);
    }, [markThreadsUnread]);
    return { markThreadOpenedFromList, markThreadUnread, markThreadsRead, markThreadsUnread };
}
//# sourceMappingURL=useMarkThreadRead.js.map